import { React, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DataTable from "examples/Tables/DataTable";
import { ToastContainer, toast } from "react-toastify";
import { Stack, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { useNavigate } from "react-router-dom";
import MDBadge from "components/MDBadge";
import nodata from "assets/images/nodata.png";
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import { hasAdmissionPermission } from "hooks/permissions";
const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

function ApplicantCorrection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [updateEmailLoading, setUpdateEmailLoading] = useState(false);
  const [cnic, setCnic] = useState("");
  const [applications, setApplications] = useState([]);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [openPhoneModal, setOpenPhoneModal] = useState(false);
  const [phoneCnicApplication, setPhoneCnicApplication] = useState(null);
  const [newPhone, setNewPhone] = useState("");
  const [newCnic, setNewCnic] = useState("");
  const [updatePhoneLoading, setUpdatePhoneLoading] = useState(false);
  const formatCNIC = (value) => {
    // Remove all non-digits
    const numbers = value.replace(/\D/g, '');
    
    // Enforce exact format: 5-7-1 digits
    if (numbers.length <= 5) {
      return numbers;
    } else if (numbers.length <= 12) {
      return `${numbers.slice(0, 5)}-${numbers.slice(5, Math.min(12, numbers.length))}`;
    } else {
      return `${numbers.slice(0, 5)}-${numbers.slice(5, 12)}-${numbers.slice(12, 13)}`;
    }
  };

  const handleCnicChange = (e) => {
    const inputValue = e.target.value;
    // Only allow numbers and dashes
    if (!/^[\d-]*$/.test(inputValue)) {
      return;
    }
    const formattedValue = formatCNIC(inputValue);
    setCnic(formattedValue);
  };
  const handleNewCnicChange = (e) => {
    const inputValue = e.target.value;
    // Only allow numbers and dashes
    if (!/^[\d-]*$/.test(inputValue)) {
      return;
    }
    const formattedValue = formatCNIC(inputValue);
    setNewCnic(formattedValue);
  };
  const handleOpenEmailModal = (application) => {
    setCurrentApplication(application);
    setNewEmail("");
    setOpenEmailModal(true);
  };

  const handleCloseEmailModal = () => {
    setOpenEmailModal(false);
    setCurrentApplication(null);
    setNewEmail("");
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !currentApplication) return;
    setUpdateEmailLoading(true);

    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: currentApplication.email,
          newEmail: newEmail
        }),
      };

      const response = await fetch(
        `${BASE_URL}/auth/update_email_password`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        toast.success("Email updated successfully");
        handleCloseEmailModal();
        // Refresh the applications list
        if (cnic) {
          fetchApplicationsByCnic();
        }
      } else {
        toast.error(data.message || "Failed to update email");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while updating email");
    } finally {
      setUpdateEmailLoading(false);
    }
  };

  const handleEditApplication = (application) => {
    handleOpenEmailModal(application);
  };

  const handleOpenPhoneModal = (application) => {
    setPhoneCnicApplication(application);
    setNewPhone(application.phone || "");
    setNewCnic(application.cnic || "");
    setOpenPhoneModal(true);
  };

  const handleClosePhoneModal = () => {
    setOpenPhoneModal(false);
    setPhoneCnicApplication(null);
    setNewPhone("");
    setNewCnic("");
  };

  const handleUpdatePhoneCnic = async () => {
    // Phone: must start with 03 and be 11 digits
    if (!/^03\d{9}$/.test(newPhone)) {
      toast.error("Phone must start with 03 and be 11 digits");
      return;
    }
    // CNIC: XXXXX-XXXXXXX-X
    if (!/^\d{5}-\d{7}-\d{1}$/.test(newCnic)) {
      toast.error("CNIC must be in format XXXXX-XXXXXXX-X");
      return;
    }
    setUpdatePhoneLoading(true);
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: phoneCnicApplication.id,
          phone: newPhone,
          cnic: newCnic,
        }),
      };
      const response = await fetch(
        `${BASE_URL}/applicant/update_phone_cnic`,
        requestOptions
      );
      const data = await response.json();
      if (response.ok) {
        toast.success("Phone and CNIC updated successfully");
        handleClosePhoneModal();
        // Refresh the applications list
        if (newCnic) {
          fetchApplicationsByCnicUpdate(newCnic);
        }
      } else {
        toast.error(data.error || "Failed to update phone/cnic");
      }
    } catch (error) {
      toast.error("An error occurred while updating phone/cnic");
    } finally {
      setUpdatePhoneLoading(false);
    }
  };

  const fetchApplicationsByCnic = async () => {
    // Check for exact CNIC format: 5-7-1 digits
    const cnicRegex = /^(\d{5})-(\d{7})-(\d{1})$/;
    if (!cnicRegex.test(cnic)) {
      toast.error("Please enter CNIC in correct format: XXXXX-XXXXXXX-X");
      return;
    }

    setLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        `${BASE_URL}/auth/get_applicants_by_cnic?cnic=${cnic}`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        setApplications(data.applicants || []);
        if (data.applicants?.length === 0) {
          toast.info("No applications found for this CNIC");
        }
      } else {
        toast.error(data.error || "Application not found");
        setApplications([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching applications");
    } finally {
      setLoading(false);
    }
  };
  const fetchApplicationsByCnicUpdate = async (newCnic) => {
    // Check for exact CNIC format: 5-7-1 digits
    const cnicRegex = /^(\d{5})-(\d{7})-(\d{1})$/;
    if (!cnicRegex.test(cnic)) {
      toast.error("Please enter CNIC in correct format: XXXXX-XXXXXXX-X");
      return;
    }

    setLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        `${BASE_URL}/auth/get_applicants_by_cnic?cnic=${newCnic}`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        setApplications(data.applicants || []);
        if (data.applicants?.length === 0) {
          toast.info("No applications found for this CNIC");
        }
      } else {
        toast.error(data.message || "Failed to fetch applications");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching applications");
    } finally {
      setLoading(false);
    }
  };
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Applicant Name", accessor: "applicant_name", align: "center" },
    { Header: "email", accessor: "email", align: "center" },
    { Header: "CNIC", accessor: "cnic", align: "center" },
    { Header: "Phone", accessor: "phone", align: "center" },
    {
      Header: "Actions", accessor: "actions", align: "center"
    },
  ];

  const rows = applications.length > 0
    ? applications.map((app, index) => ({
        serial: index + 1,
        id: app.id,
        applicant_name: app.name,
        email: app.email,
        cnic: app.cnic,
        phone: app.phone,
        actions: (
          <>
          {hasAdmissionPermission("Applicant Correction", "UPDATE") && (
            <IconButton 
              onClick={() => handleEditApplication(app)}
              color="info"
              title="Edit Email"
            >
              <EditIcon />
            </IconButton>
          )}
          {hasAdmissionPermission("Applicant Correction", "UPDATE") && (
            <IconButton
              onClick={() => handleOpenPhoneModal(app)}
              color="secondary"
              title="Edit Phone/CNIC"
            >
              <PhoneAndroidIcon />
            </IconButton>
          )}
          </>
        ),
      }))
    : [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Search Applicants by CNIC
                </MDTypography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Enter CNIC"
                      variant="outlined"
                      value={cnic}
                      onChange={handleCnicChange}
                      placeholder="XXXXX-XXXXXXX-X"
                      inputProps={{ 
                        maxLength: 15,
                        pattern: "\\d{5}-\\d{7}-\\d{1}"
                      }}
                      helperText="Format: XXXXX-XXXXXXX-X"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={fetchApplicationsByCnic}
                      disabled={loading}
                      fullWidth
                      style={{ color: "white" }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Fetch"}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <Card sx={{ mt: 3 }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Applicants Results
                </MDTypography>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress />
                  </MDBox>
                ) : applications.length > 0 ? (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                    <img
                      src={nodata}
                      style={{
                        width: "20%",
                        height: "20%",
                      }}
                      alt="No Data"
                    />
                    <MDTypography variant="body1" mt={2}>
                      No Applicants found. Please enter a valid CNIC and try again.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Modal
        open={openEmailModal}
        onClose={handleCloseEmailModal}
        aria-labelledby="email-update-modal"
        aria-describedby="modal-to-update-email"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
        }}>
          <MDTypography variant="h6" component="h2" gutterBottom>
            Update Email Address
          </MDTypography>
          
          {currentApplication && (
            <MDBox mb={2}>
              <MDTypography variant="body2" color="text.secondary">
                Current Email: {currentApplication.email}
              </MDTypography>
            </MDBox>
          )}

          <TextField
            fullWidth
            label="New Email Address"
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            type="email"
            sx={{ mb: 3 }}
          />

          <DialogActions>
            <Button onClick={handleCloseEmailModal}>Cancel</Button>
            <Button 
              onClick={handleUpdateEmail} 
              variant="contained" 
              color="primary"
              style={{ color: "white" }}
              disabled={!newEmail || updateEmailLoading}
            >
              {updateEmailLoading ? (
                <CircularProgress size={24} style={{ color: "white" }} />
              ) : (
                "Update Email and Send Email"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Modal>

      <Modal
        open={openPhoneModal}
        onClose={handleClosePhoneModal}
        aria-labelledby="phone-cnic-update-modal"
        aria-describedby="modal-to-update-phone-cnic"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
        }}>
          <MDTypography variant="h6" component="h2" gutterBottom>
            Update Phone & CNIC
          </MDTypography>
          {phoneCnicApplication && (
            <MDBox mb={2}>
              <MDTypography variant="body2" color="text.secondary">
                Current Phone: {phoneCnicApplication.phone}<br/>
                Current CNIC: {phoneCnicApplication.cnic}
              </MDTypography>
            </MDBox>
          )}
          <TextField
            fullWidth
            label="New Phone"
            variant="outlined"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="03XXXXXXXXX"
            inputProps={{ maxLength: 11, pattern: "03[0-9]{9}" }}
            helperText="Format: 03XXXXXXXXX"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="New CNIC"
            variant="outlined"
            value={newCnic}
            onChange={handleNewCnicChange}
            placeholder="XXXXX-XXXXXXX-X"
            inputProps={{ 
              maxLength: 15,
              pattern: "\\d{5}-\\d{7}-\\d{1}"
            }}
            helperText="Format: XXXXX-XXXXXXX-X"
            sx={{ mb: 3 }}
          />
          <DialogActions>
            <Button onClick={handleClosePhoneModal}>Cancel</Button>
            <Button 
              onClick={handleUpdatePhoneCnic} 
              variant="contained" 
              color="secondary"
              style={{ color: "white" }}
              disabled={updatePhoneLoading}
            >
              {updatePhoneLoading ? (
                <CircularProgress size={24} style={{ color: "white" }} />
              ) : (
                "Update"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Modal>
    </DashboardLayout>
  );
}

export default ApplicantCorrection;